import { InfoCircleFilled } from '@ant-design/icons';
import { Alert, Button, Form, InputNumber, Modal } from 'antd';
import { useContext, useMemo, useRef } from 'react';
import { useStore } from 'zustand';

import { SwitchableInput } from 'app/components/commons/SwitchableInput/SwitchableInput';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { useSaveMaxBookings } from 'app/hooks/data/extranet/inventory/useMaxBookings';

import { inventoryStore } from '../../InventoryStore';

import './MaxBookingConfigurationModal.scss';

export type FormValuesType = {
  maxBookings: {
    packageId: number;
    maxBooking?: number;
    isSwitchOn: boolean;
  }[];
};

export const MaxBookingConfigurationModal = () => {
  const [form] = Form.useForm<FormValuesType>();
  const formValues = Form.useWatch('maxBookings', form);
  const { hotelId } = useContext(HotelContext);
  const { publishedPackages } = useContext(LegacyHotelContext);

  const setOpenEditMaxBooking = useStore(
    inventoryStore,
    (state) => state.setOpenEditMaxBooking
  );

  const { mutateAsync: saveMaxBookings } = useSaveMaxBookings(hotelId);

  const userHasToggledRef = useRef(false);

  const showAlert = useMemo(() => {
    if (!userHasToggledRef.current) {
      return false;
    }

    return formValues?.some((item) => !item.isSwitchOn) ?? false;
  }, [formValues]);

  const validationIsDisabled = useMemo(() => {
    return (
      formValues?.some(
        (item) => item.isSwitchOn && item.maxBooking === undefined
      ) ?? false
    );
  }, [formValues]);

  const handleSave = async (values: FormValuesType) => {
    const formattedValues = {
      maxBookings: values.maxBookings.map((item) => ({
        ...item,
        maxBooking: item.isSwitchOn ? item.maxBooking : undefined,
      })),
    };

    await saveMaxBookings(formattedValues);
    setOpenEditMaxBooking(false);
  };

  const handleCancel = () => {
    setOpenEditMaxBooking(false);
  };

  const toggleSwitch = (value: boolean, name: number) => {
    if (!userHasToggledRef.current && !value) {
      userHasToggledRef.current = true;
    }

    form.setFieldValue(['maxBookings', name, 'isSwitchOn'], value);

    if (!value) {
      form.setFieldValue(['maxBookings', name, 'maxBooking'], undefined);
    }
  };

  const initialValues = {
    maxBookings: publishedPackages.map((pkg) => ({
      packageId: pkg.id,
      maxBooking: pkg.defaultMaxBooking,
      isSwitchOn: pkg.defaultMaxBooking !== undefined,
    })),
  };

  return (
    <Modal
      width={466}
      open={true}
      title={'Define max booking'}
      onOk={form.submit}
      maskClosable={false}
      onCancel={handleCancel}
      closable={true}
      className="max-booking-modal"
      footer={[
        <Button size="large" type="ghost" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          size="large"
          type="primary"
          htmlType="submit"
          onClick={form.submit}
          disabled={validationIsDisabled}
        >
          Save
        </Button>,
      ]}
      centered
      destroyOnClose={true}
    >
      <Form<FormValuesType>
        initialValues={initialValues}
        form={form}
        onFinish={handleSave}
        layout="vertical"
        className="max-booking-modal_content_form_items"
        name="max-booking-form"
        preserve={true}
      >
        <div className="max-booking-modal_content">
          <div className="max-booking-modal_content_header">
            Define the maximum number of bookings{' '}
            <span className="bold-text">that can be made in a day</span>. It
            will be applied{' '}
            <span className="bold-text">automatically to all days</span>, but
            can still be edited for each day.
          </div>
          <div className="max-booking-modal_content_form">
            <div className="max-booking-modal_content_form_title">
              Enable / Disabled max booking
            </div>
            {showAlert && (
              <Alert
                type="warning"
                message="All your max bookings data will be overwritten except those entered manually."
                icon={<InfoCircleFilled />}
                showIcon
              />
            )}

            <Form.List name={['maxBookings']}>
              {(pkgs) => (
                <>
                  {pkgs.map(({ key, name, ...restField }) => {
                    if (!formValues) {
                      return null;
                    }

                    const formPackage = formValues[name];

                    const pkg = publishedPackages.find(
                      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
                      (pkg) => pkg.id === formPackage.packageId
                    );

                    return (
                      <SwitchableInput
                        name={
                          pkg?.aliasForHotel ??
                          `Package #${formPackage.packageId}`
                        }
                        key={formPackage.packageId}
                        onChange={(value: boolean) => {
                          toggleSwitch(value, name);
                        }}
                        value={formPackage.isSwitchOn}
                        childNode={
                          <div className="max-booking-modal_content_form_items_item">
                            <Form.Item
                              {...restField}
                              label="Max booking"
                              name={[name, 'maxBooking']}
                            >
                              <InputNumber
                                min={0}
                                placeholder="Enter max booking"
                                disabled={!formPackage.isSwitchOn}
                              />
                            </Form.Item>
                          </div>
                        }
                      />
                    );
                  })}
                </>
              )}
            </Form.List>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
